import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, Check } from 'lucide-react';
import { Button } from './ui/button';

interface FileUploadProps {
  onUpload: (url: string) => void;
  label?: string;
  accept?: Record<string, string[]>;
  apartmentId?: number;
}

export function FileUpload({ onUpload, label = "Upload Image", accept = { 'image/*': [] }, apartmentId }: FileUploadProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;

    setIsUploading(true);
    setError(null);

    const localPreview = URL.createObjectURL(file);
    setPreview(localPreview);

    try {
      const formData = new FormData();
      formData.append('file', file);

      let uploadUrl = '/api/upload';
      if (apartmentId) {
        formData.append('apartmentId', String(apartmentId));
        uploadUrl = '/api/upload/watermarked';
      }

      const res = await fetch(uploadUrl, {
        method: 'POST',
        body: formData,
      });

      if (!res.ok) throw new Error('Upload failed');

      const data = await res.json();
      onUpload(data.url);
    } catch (err) {
      setError('Upload failed. Please try again.');
      setPreview(null);
    } finally {
      setIsUploading(false);
    }
  }, [onUpload]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024,
  });

  if (preview && !error) {
    return (
      <div className="relative rounded-md overflow-hidden aspect-video border bg-muted">
        <img src={preview} alt="Preview" className="w-full h-full object-cover" />
        <Button
          size="icon"
          variant="destructive"
          className="absolute top-2 right-2 rounded-full"
          onClick={(e) => {
            e.stopPropagation();
            setPreview(null);
            onUpload("");
          }}
          data-testid="button-remove-upload"
        >
          <X className="w-4 h-4" />
        </Button>
        <div className="absolute bottom-2 right-2 bg-green-600 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
          <Check className="w-3 h-3" /> Uploaded
        </div>
      </div>
    );
  }

  return (
    <div>
      <div
        {...getRootProps()}
        data-testid="dropzone-upload"
        className={`
          border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors
          flex flex-col items-center justify-center gap-2 min-h-[120px]
          ${isDragActive ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'}
        `}
      >
        <input {...getInputProps()} data-testid="input-file-upload" />
        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
          {isUploading ? (
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          ) : (
            <Upload className="w-5 h-5 text-muted-foreground" />
          )}
        </div>
        <div className="text-sm text-muted-foreground font-medium">
          {isUploading ? "Uploading..." : (isDragActive ? "Drop here" : label)}
        </div>
      </div>
      {error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
}
